// Apolo IDE — MultiAgent History tab (Sprint v4 Epic S5 WS S5.3).
//
// Lists past dispatches the user has run (compare / decompose). Calls
// GET /api/agents/dispatch?status=completed,failed,cancelled&limit=50
// then renders compact rows with kind / status / cost / age. Clicking a
// row exposes the dispatch id so the parent can open the detailed
// inspector (S3's DispatchStatusBar + SubagentTracker + SynthesisView).

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, CheckCircle, Ban, XCircle, RotateCw, Cpu } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';

const DEFAULT_STATUSES = ['completed', 'failed', 'cancelled'];
const DEFAULT_LIMIT = 50;

function fmtUsd(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '$0';
  if (n < 0.001) return '< $0.001';
  return `$${n.toFixed(n < 0.01 ? 4 : 3)}`;
}

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (!Number.isFinite(d)) return '';
  const diffSec = Math.max(0, Math.round((Date.now() - d) / 1000));
  if (diffSec < 60) return `${diffSec}s atrás`;
  const min = Math.round(diffSec / 60);
  if (min < 60) return `${min}m atrás`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h atrás`;
  const day = Math.round(hr / 24);
  return `${day}d atrás`;
}

function statusIcon(status) {
  switch (status) {
    case 'completed':
      return <CheckCircle size={11} className="text-apolo-success" aria-hidden="true" />;
    case 'failed':
      return <XCircle size={11} className="text-apolo-error" aria-hidden="true" />;
    case 'cancelled':
      return <Ban size={11} className="text-apolo-warning" aria-hidden="true" />;
    case 'running':
      return <Loader2 size={11} className="animate-spin text-apolo-accent" aria-hidden="true" />;
    default:
      return <Cpu size={11} className="text-apolo-textTertiary" aria-hidden="true" />;
  }
}

async function defaultFetcher({ statuses, limit }) {
  const params = new URLSearchParams();
  if (statuses && statuses.length) params.set('status', statuses.join(','));
  if (limit) params.set('limit', String(limit));
  return apiFetch(`/api/agents/dispatch?${params.toString()}`);
}

export default function HistoryTab({
  onSelect,
  fetcher = defaultFetcher,
  statuses = DEFAULT_STATUSES,
  limit = DEFAULT_LIMIT,
  autoLoad = true,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetcher({ statuses, limit });
      if (!res.ok) {
        const m = (res.data && res.data.error) || res.error || 'Falha ao carregar histórico';
        setError(m);
        setItems([]);
        return;
      }
      const data = res.data || res;
      setItems(Array.isArray(data.dispatches) ? data.dispatches : []);
    } catch (err) {
      const m = err && err.message ? err.message : String(err);
      setError(m);
    } finally {
      setLoading(false);
    }
  }, [fetcher, statuses, limit]);

  useEffect(() => {
    if (!autoLoad) return;
    load();
  }, [load, autoLoad]);

  if (loading && items.length === 0) {
    return (
      <div
        data-testid="history-loading"
        className="flex items-center justify-center gap-2 px-3 py-6 text-xs text-apolo-textTertiary"
      >
        <Loader2 size={12} className="animate-spin" aria-hidden="true" />
        Carregando histórico...
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="history-error"
        role="alert"
        className="m-3 flex items-start gap-2 rounded-md border border-apolo-error/40 bg-apolo-error/10 p-2 text-[11px] text-apolo-error"
      >
        <AlertCircle size={12} className="mt-0.5" aria-hidden="true" />
        <div className="flex-1">
          <p>{error}</p>
          <button
            type="button"
            onClick={load}
            className="mt-1 inline-flex items-center gap-1 text-[10px] underline"
          >
            <RotateCw size={10} aria-hidden="true" /> Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div
        data-testid="history-empty"
        className="m-3 rounded-md border border-dashed border-apolo-borderSubtle bg-apolo-surface1 p-4 text-center text-xs text-apolo-textTertiary"
      >
        Nenhum dispatch concluído ainda. Use Compare ou Decompose para começar.
      </div>
    );
  }

  return (
    <section aria-label="Histórico de dispatches" data-testid="history-tab" className="flex flex-col">
      <header className="flex items-center justify-between border-b border-apolo-borderSubtle px-3 py-1.5">
        <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
          {items.length} dispatch{items.length === 1 ? '' : 'es'}
        </span>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary"
          aria-label="Recarregar histórico"
        >
          <RotateCw size={10} aria-hidden="true" /> Refresh
        </button>
      </header>
      <ul role="list" className="flex flex-col">
        {items.map((d) => (
          <li
            key={d.id}
            data-testid="history-item"
            data-dispatch-id={d.id}
            className="border-b border-apolo-borderSubtle/50 last:border-b-0"
          >
            <button
              type="button"
              onClick={() => onSelect?.(d)}
              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition hover:bg-apolo-surface1"
            >
              <div className="flex min-w-0 items-center gap-2">
                {statusIcon(d.status)}
                <span className="text-[10px] uppercase tracking-wide text-apolo-textTertiary">
                  {d.kind}
                </span>
                <span className="truncate text-[11px] text-apolo-textPrimary">
                  {(d.spec_json && (d.spec_json.summary || d.spec_json.prompt)) || d.id.slice(0, 8)}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2 text-[10px] text-apolo-textTertiary">
                <span className="font-mono">{fmtUsd(d.actual_cost_usd || d.estimated_cost_usd)}</span>
                <span>{fmtRelativeTime(d.created_at)}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
